import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  BarChart3, Users, Building2, Calendar,
  Globe, Briefcase, UserCheck, Layers, Timer, TrendingUp, Cake, Gift
} from 'lucide-react';

function HBar({ label, value, max, total, color = 'bg-teal-500', showPct = false }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  const displayPct = total > 0 ? (value / total) * 100 : pct;
  const w = Math.max(pct, 2) + '%';
  return (
    <div className="flex items-center gap-2 py-1">
      <span className="text-xs text-slate-600 w-[280px] truncate flex-shrink-0 uppercase" title={label}>{label}</span>
      <div className="flex-1 bg-slate-100 rounded-full h-5 overflow-hidden">
        <div className={color + ' h-5 rounded-full flex items-center justify-end pr-2 transition-all duration-500'} style={{ width: w }}>
          {pct > 15 && <span className="text-[10px] text-white font-bold">{value}</span>}
        </div>
      </div>
      {pct <= 15 && <span className="text-xs text-slate-500 w-8 text-right">{value}</span>}
      {showPct && <span className="text-[10px] text-slate-400 w-10 text-right">{displayPct.toFixed(1)}%</span>}
    </div>
  );
}

function StatTable({ headers, rows, testId }) {
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200">
            {headers.map((h, i) => (
              <th key={i} className={'py-2 px-3 text-left text-xs font-semibold text-slate-500 uppercase ' + (i > 0 ? 'text-right' : '')}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
              {row.map((cell, ci) => (
                <td key={ci} className={'py-2 px-3 ' + (ci > 0 ? 'text-right font-medium' : '')}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, color = 'text-teal-500' }) {
  return (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={'p-2 rounded-lg bg-slate-50 ' + color}><Icon className="w-5 h-5" /></div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function getMax(arr, key) {
  if (!arr || arr.length === 0) return 1;
  let m = 0;
  for (let i = 0; i < arr.length; i++) {
    const v = arr[i][key];
    if (v > m) m = v;
  }
  return m || 1;
}

function sumField(arr, key) {
  let s = 0;
  for (let i = 0; i < arr.length; i++) s += arr[i][key] || 0;
  return s;
}

function ResumenTab({ data, maxGer, maxNac }) {
  const tp = data.total_personas;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Users} label="Total Personas" value={tp} />
        <KpiCard icon={Building2} label="Gerencias" value={data.por_gerencia.length} color="text-copper-500" />
        <KpiCard icon={Briefcase} label="Cargos Unicos" value={data.por_cargo.length + '+'} color="text-indigo-500" />
        <KpiCard icon={Calendar} label="Antigüedad Prom." value={data.antiguedad_promedio_anios + ' años'} color="text-green-600" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Personas por Gerencia</CardTitle></CardHeader>
          <CardContent className="pt-0">
            {data.por_gerencia.map((g) => <HBar key={g.nombre} label={g.nombre} value={g.cantidad} max={maxGer} total={tp} showPct />)}
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Distribución por Género</CardTitle></CardHeader>
          <CardContent className="pt-0">
            {data.genero_global.map((g) => (
              <HBar key={g.sexo} label={g.sexo} value={g.cantidad} max={tp} total={tp}
                color={g.sexo === 'Masculino' ? 'bg-teal-500' : g.sexo === 'Femenino' ? 'bg-copper-500' : 'bg-slate-400'} showPct />
            ))}
            <div className="mt-3 border-t pt-3">
              <p className="text-xs text-slate-500 mb-2 font-medium">Por Nacionalidad (top 5)</p>
              {data.por_nacionalidad.slice(0, 5).map((n) => (
                <HBar key={n.nacionalidad} label={n.nacionalidad} value={n.cantidad} max={maxNac} total={tp} color="bg-indigo-500" showPct />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function GerenciasTab({ data, maxGer, maxSup }) {
  const tp = data.total_personas;
  return (
    <div className="space-y-4">
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Personas por Gerencia</CardTitle></CardHeader>
        <CardContent className="pt-0">
          {data.por_gerencia.map((g) => <HBar key={g.nombre} label={g.nombre} value={g.cantidad} max={maxGer} total={tp} showPct />)}
        </CardContent>
      </Card>
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Personas por Superintendencia</CardTitle></CardHeader>
        <CardContent className="pt-0 max-h-[500px] overflow-y-auto">
          {data.por_superintendencia.map((s) => <HBar key={s.nombre} label={s.nombre} value={s.cantidad} max={maxSup} total={tp} color="bg-copper-500" showPct />)}
        </CardContent>
      </Card>
    </div>
  );
}

function GeneroTab({ data }) {
  const genderRows = data.genero_por_gerencia.map((g) => {
    const total = g.masculino + g.femenino + g.otro;
    const pct = total > 0 ? (g.femenino / total * 100).toFixed(1) + '%' : '0%';
    return [g.gerencia, g.masculino, g.femenino, total, pct];
  });
  const superRows = data.genero_por_superintendencia.map((s) => {
    const total = s.masculino + s.femenino + s.otro;
    const pct = total > 0 ? (s.femenino / total * 100).toFixed(1) + '%' : '0%';
    return [s.superintendencia, s.masculino, s.femenino, total, pct];
  });
  return (
    <div className="space-y-4">
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Género Global</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-3 gap-4 mb-4">
            {data.genero_global.map((g) => (
              <div key={g.sexo} className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">{g.cantidad}</p>
                <p className="text-xs text-slate-500">{g.sexo}</p>
                <p className="text-[10px] text-slate-400">{(g.cantidad / data.total_personas * 100).toFixed(1)}%</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Género por Gerencia</CardTitle></CardHeader>
        <CardContent className="pt-0">
          <StatTable testId="genero-gerencia-table"
            headers={['Gerencia', 'Masculino', 'Femenino', 'Total', '% Femenino']}
            rows={genderRows} />
        </CardContent>
      </Card>
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Género por Superintendencia</CardTitle></CardHeader>
        <CardContent className="pt-0 max-h-[500px] overflow-y-auto">
          <StatTable testId="genero-super-table"
            headers={['Superintendencia', 'Masc.', 'Fem.', 'Total', '% Fem.']}
            rows={superRows} />
        </CardContent>
      </Card>
    </div>
  );
}

function NacionalidadTab({ data, maxNac }) {
  const tp = data.total_personas;
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-2"><CardTitle className="text-sm">Distribución por Nacionalidad</CardTitle></CardHeader>
      <CardContent className="pt-0">
        {data.por_nacionalidad.map((n) => <HBar key={n.nacionalidad} label={n.nacionalidad} value={n.cantidad} max={maxNac} total={tp} color="bg-indigo-500" showPct />)}
      </CardContent>
    </Card>
  );
}

function JornadasTab({ data, maxJor }) {
  const tp = data.total_personas;
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-2"><CardTitle className="text-sm">Distribución por Jornada</CardTitle></CardHeader>
      <CardContent className="pt-0">
        {data.por_jornada.map((j) => <HBar key={j.jornada} label={j.jornada} value={j.cantidad} max={maxJor} total={tp} color="bg-amber-500" showPct />)}
      </CardContent>
    </Card>
  );
}

function CargosTab({ data, maxCargo }) {
  const tp = data.total_personas;
  const maxNivel = getMax(data.por_nivel, 'cantidad');
  return (
    <div className="space-y-4">
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Personas por Nivel Jerárquico</CardTitle></CardHeader>
        <CardContent className="pt-0">
          {data.por_nivel.map((n) => <HBar key={n.nivel} label={'Nivel ' + n.nivel} value={n.cantidad} max={maxNivel} total={tp} color="bg-violet-500" showPct />)}
        </CardContent>
      </Card>
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Top 20 Cargos con más Personas</CardTitle></CardHeader>
        <CardContent className="pt-0">
          {data.por_cargo.map((c) => <HBar key={c.cargo} label={c.cargo} value={c.cantidad} max={maxCargo} total={tp} color="bg-teal-500" showPct />)}
        </CardContent>
      </Card>
    </div>
  );
}

function JefesTab({ data }) {
  const rows = data.top_jefes.map((j) => [j.jefe_nombre, j.cargo_nombre, j.gerencia_nombre || '-', j.personas_a_cargo]);
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="pb-2"><CardTitle className="text-sm">Top 20 Personas con más Personal a Cargo</CardTitle></CardHeader>
      <CardContent className="pt-0">
        <StatTable testId="top-jefes-table"
          headers={['Jefe', 'Cargo', 'Gerencia', 'Personas a Cargo']}
          rows={rows} />
      </CardContent>
    </Card>
  );
}

function AntiguedadTab({ data, maxAnt }) {
  const totalConFecha = sumField(data.por_anio_ingreso, 'cantidad');
  const tp = data.total_personas;
  const anios = data.por_anio_ingreso;
  const rangoAnios = anios.length > 0 ? (anios[anios.length - 1].anio - anios[0].anio + 1) : 0;
  const maxAnioIngreso = getMax(anios, 'cantidad');
  const reversed = anios.slice().reverse();
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KpiCard icon={Calendar} label="Antigüedad Promedio" value={data.antiguedad_promedio_anios + ' años'} />
        <KpiCard icon={Users} label="Con Fecha Ingreso" value={totalConFecha} color="text-green-600" />
        <KpiCard icon={TrendingUp} label="Años de Datos" value={rangoAnios + ' años'} color="text-indigo-500" />
      </div>
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Distribución por Rango de Antigüedad</CardTitle></CardHeader>
        <CardContent className="pt-0">
          {data.por_antiguedad.map((a) => <HBar key={a.rango} label={a.rango} value={a.cantidad} max={maxAnt} total={tp} color="bg-green-500" showPct />)}
        </CardContent>
      </Card>
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-sm">Ingresos por Año</CardTitle></CardHeader>
        <CardContent className="pt-0 max-h-[400px] overflow-y-auto">
          {reversed.map((a) => <HBar key={a.anio} label={String(a.anio)} value={a.cantidad} max={maxAnioIngreso} total={totalConFecha} color="bg-teal-500" showPct />)}
        </CardContent>
      </Card>
    </div>
  );
}

const MESES_ES = {
  'January': 'Enero', 'February': 'Febrero', 'March': 'Marzo', 'April': 'Abril',
  'May': 'Mayo', 'June': 'Junio', 'July': 'Julio', 'August': 'Agosto',
  'September': 'Septiembre', 'October': 'Octubre', 'November': 'Noviembre', 'December': 'Diciembre'
};

function traducirMes(mesEn) {
  if (!mesEn) return '';
  for (const [en, es] of Object.entries(MESES_ES)) {
    if (mesEn.includes(en)) return mesEn.replace(en, es);
  }
  return mesEn;
}

const MESES_CORTOS = ['ENE','FEB','MAR','ABR','MAY','JUN','JUL','AGO','SEP','OCT','NOV','DIC'];

function FechaCumple({ fecha }) {
  const parts = fecha.split('-');
  const dia = parts[0];
  const mesIdx = parseInt(parts[1], 10) - 1;
  const mes = MESES_CORTOS[mesIdx] || parts[1];
  return (
    <div className="flex flex-col items-center justify-center bg-slate-100 rounded-lg w-12 h-12 flex-shrink-0">
      <span className="text-sm font-bold text-slate-800 leading-none">{dia}</span>
      <span className="text-[10px] font-medium text-slate-500 leading-none mt-0.5">{mes}</span>
    </div>
  );
}

function CumpleanosTab({ data }) {
  const tp = data.total_personas;
  const cumpleMes = data.cumpleanos_mes || [];
  const proximos = data.proximos_cumpleanos || [];
  const porMes = data.cumpleanos_por_mes || [];
  const porEdad = data.por_rango_edad || [];
  const maxMes = getMax(porMes, 'cantidad');
  const maxEdad = getMax(porEdad, 'cantidad');
  const mesActual = traducirMes(data.mes_actual || '');
  const hoy = proximos.filter((p) => p.dias_restantes === 0);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Cake} label="Edad Promedio" value={data.edad_promedio + ' años'} />
        <KpiCard icon={Gift} label={'Cumpleaños en ' + mesActual.split(' ')[0]} value={cumpleMes.length} color="text-pink-500" />
        <KpiCard icon={Calendar} label="Hoy cumplen años" value={hoy.length} color="text-amber-500" />
        <KpiCard icon={Users} label="Próximos 30 días" value={proximos.length} color="text-indigo-500" />
      </div>

      {hoy.length > 0 && (
        <Card className="border-0 shadow-sm bg-amber-50">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-700">Hoy cumplen años</CardTitle></CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {hoy.map((p, i) => (
                <div key={i} className="flex items-center gap-3 p-2 bg-white rounded-lg">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Cake className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate">{p.nombre_completo}</p>
                    <p className="text-xs text-slate-500 truncate">{p.cargo_nombre || '-'}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="w-12 text-right">
                      <p className="text-lg font-bold text-amber-600 leading-tight">{p.edad_cumple}</p>
                      <p className="text-[10px] text-slate-400">años</p>
                    </div>
                    <div className="text-right hidden md:block w-48">
                      <p className="text-xs text-slate-600 truncate">{p.gerencia_nombre || '-'}</p>
                      <p className="text-[10px] text-slate-400 truncate">{p.superintendencia_nombre || '-'}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Próximos cumpleaños (30 días)</CardTitle></CardHeader>
          <CardContent className="pt-0 max-h-[500px] overflow-y-auto">
            {proximos.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">Sin cumpleaños próximos</p>
            ) : (
              <div className="space-y-1.5">
                {proximos.map((p, i) => (
                  <div key={i} className="flex items-center gap-2 py-1.5 border-b border-slate-50" data-testid={'proximo-cumple-' + i}>
                    <FechaCumple fecha={p.fecha_cumple} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-800 truncate">{p.nombre_completo}</p>
                      <p className="text-[10px] text-slate-400 truncate">{p.cargo_nombre} - {p.gerencia_nombre}</p>
                    </div>
                    <span className="text-sm font-semibold text-teal-600 flex-shrink-0">{p.edad_cumple} años</span>
                    {p.dias_restantes === 0 ? (
                      <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">HOY</span>
                    ) : (
                      <span className="text-[10px] text-slate-400 w-14 text-right flex-shrink-0">en {p.dias_restantes}d</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm">{'Cumpleaños en ' + mesActual}</CardTitle></CardHeader>
          <CardContent className="pt-0 max-h-[500px] overflow-y-auto">
            {cumpleMes.length === 0 ? (
              <p className="text-sm text-slate-400 py-4 text-center">Sin cumpleaños este mes</p>
            ) : (
              <StatTable testId="cumpleanos-mes-table"
                headers={['Día', 'Nombre', 'Edad', 'Cargo', 'Gerencia']}
                rows={cumpleMes.map((p) => [
                  p.dia,
                  p.nombre_completo,
                  p.edad_cumple,
                  p.cargo_nombre || '-',
                  p.gerencia_nombre || '-'
                ])} />
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Cumpleaños por Mes del Año</CardTitle></CardHeader>
          <CardContent className="pt-0">
            {porMes.map((m) => <HBar key={m.mes} label={m.mes} value={m.cantidad} max={maxMes} total={tp} color="bg-pink-500" showPct />)}
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="text-sm">Distribución por Rango de Edad</CardTitle></CardHeader>
          <CardContent className="pt-0">
            {porEdad.map((e) => <HBar key={e.rango} label={e.rango} value={e.cantidad} max={maxEdad} total={tp} color="bg-teal-500" showPct />)}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

const SUB_TABS = [
  { id: 'resumen', label: 'Resumen', icon: BarChart3 },
  { id: 'gerencias', label: 'Gerencias', icon: Building2 },
  { id: 'genero', label: 'Género', icon: Users },
  { id: 'nacionalidad', label: 'Nacionalidad', icon: Globe },
  { id: 'jornadas', label: 'Jornadas', icon: Timer },
  { id: 'cargos', label: 'Cargos y Niveles', icon: Layers },
  { id: 'jefes', label: 'Jefaturas', icon: UserCheck },
  { id: 'antiguedad', label: 'Antigüedad', icon: Calendar },
  { id: 'cumpleanos', label: 'Cumpleaños', icon: Cake },
];

export function EstadisticasLB({ data, subTab, setSubTab }) {
  const maxGer = data.por_gerencia.length > 0 ? data.por_gerencia[0].cantidad : 1;
  const maxSup = data.por_superintendencia.length > 0 ? data.por_superintendencia[0].cantidad : 1;
  const maxCargo = data.por_cargo.length > 0 ? data.por_cargo[0].cantidad : 1;
  const maxNac = data.por_nacionalidad.length > 0 ? data.por_nacionalidad[0].cantidad : 1;
  const maxJor = data.por_jornada.length > 0 ? data.por_jornada[0].cantidad : 1;
  const maxAnt = getMax(data.por_antiguedad, 'cantidad');

  return (
    <div className="space-y-4" data-testid="estadisticas-lb">
      <div className="flex gap-1 flex-wrap">
        {SUB_TABS.map((t) => {
          const TabIcon = t.icon;
          return (
            <button key={t.id} onClick={() => setSubTab(t.id)}
              data-testid={'subtab-' + t.id}
              className={'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ' +
                (subTab === t.id ? 'bg-teal-500 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}>
              <TabIcon className="w-3.5 h-3.5" />{t.label}
            </button>
          );
        })}
      </div>

      {subTab === 'resumen' && <ResumenTab data={data} maxGer={maxGer} maxNac={maxNac} />}
      {subTab === 'gerencias' && <GerenciasTab data={data} maxGer={maxGer} maxSup={maxSup} />}
      {subTab === 'genero' && <GeneroTab data={data} />}
      {subTab === 'nacionalidad' && <NacionalidadTab data={data} maxNac={maxNac} />}
      {subTab === 'jornadas' && <JornadasTab data={data} maxJor={maxJor} />}
      {subTab === 'cargos' && <CargosTab data={data} maxCargo={maxCargo} />}
      {subTab === 'jefes' && <JefesTab data={data} />}
      {subTab === 'antiguedad' && <AntiguedadTab data={data} maxAnt={maxAnt} />}
      {subTab === 'cumpleanos' && <CumpleanosTab data={data} />}
    </div>
  );
}
